import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Tooltip, EnhancedTooltip } from "rhombus-react";
import { get } from 'lodash';

export const MULTIPLE_ENTRIES = '[Multiple Entries]';

var UID = 0;

function generateUnique({ name = "UID" }){
	// Increment the UID prior to everything
	UID = UID + 1;

	return `${name}--${UID}`;
}


const PROPTYPE_THEME = PropTypes.oneOf([
	'default',
	'primary',
	'white',
	'blue',
	'purple',
	'pink',
	'orange',
	'green',
	'red',
	'grey',
	'yellow',
	'teal',
]);

export const getValueLength = (value = '', inputValue) => {
	if (typeof inputValue === 'string') {
		if (inputValue.length) {
			return inputValue.length;
		} else {
			const selectedValue = get(value, 'value', '');

			if(!isNaN(selectedValue)){
				let labelVal = get(value, 'label', '');

				if(isNaN(labelVal)){
					return 0;
				}

				return labelVal.length;
			}

			return selectedValue.length;
		}
	}

	let valLen = get(value, "length", 0);

	return valLen;
};

export const getHideCharCountRedux = ({ active, invalid, visited }, inputValue) => {
	if (!visited) {
		return true;
	}
	if (!active && !invalid) {
		return true;
	}
	if (inputValue !== undefined && !inputValue) {
		return true;
	}

	return false;
};

const isMultipleValues = ({ meta, value }) => {
	if (!value || !meta || !meta.initial) {
		return false;
	}

	const { initial } = meta;

	if (initial.hasOwnProperty('startDate') && initial.hasOwnProperty('endDate')) {
		const multipleValues = [];
		['startDate', 'endDate'].forEach((range) => {
			if (initial[range]  === MULTIPLE_ENTRIES && value[range] === MULTIPLE_ENTRIES) {
				multipleValues.push(range);
			}
		});
		// Return boolean true if both ranges are multi - otherwise specify which
		if (multipleValues.length === 1) {
			return multipleValues[0];
		} else {
			return !!multipleValues.length;
		}
	} else {
		return initial === MULTIPLE_ENTRIES && value === MULTIPLE_ENTRIES;
	}
};

function tipPropValidator(props, propName, componentName) {
	if (!props.tip) { return null; }

	let shape;
	if (props.tooltipVersion === 1) {
		shape = {
			description: PropTypes.string,
			inline:      PropTypes.bool,
			direction:   PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
			size:        PropTypes.oneOf(['small', 'medium', 'large', 'xlarge', 'fit']),
			iconSize:    PropTypes.oneOf(['xsmall', 'small', 'medium', 'large', 'xlarge']),
		};
	} else if (props.tooltipVersion === 2) { // eslint-disable-line
		shape = {
			place: PropTypes.oneOf(['top', 'right', 'bottom', 'left']).isRequired,
			type: PropTypes.oneOf(['dark', 'success', 'warning', 'error', 'info', 'light']),
			effect: PropTypes.oneOf(['float', 'solid']),
			html: PropTypes.bool,
		};
	}

	PropTypes.checkPropTypes(
		shape,
		props.tip,
		"props",
		componentName
	);
}

function ControlWrapper(Composed){
	return class Wrapper extends React.Component {
		static displayName = `${Composed.displayName}-Control-Wrapper`;

		static contextTypes = { rhombusTheme: PROPTYPE_THEME };
		static childContextTypes = { iconClassPrefix: PropTypes.string };

		static defaultProps = {
			label:     "",
			className: "",
			required:  false,
			tip:       {
				description: "",
				inline:    false,
				direction: 'left',
				size:      'fit',
				iconSize:  'small',
			},
			showPristineErrors: false,
			tooltipVersion: 1,
		};

		static propTypes = {
			/** Label to display on the given control */
			label: PropTypes.oneOfType([
				PropTypes.string,
				PropTypes.element,
				PropTypes.node,
			]).isRequired,

			/** Is this field required in the form */
			required: PropTypes.bool,

			/** Tooltip to display after the label*/
			tip: tipPropValidator,

			/** Class name for the control */
			className: PropTypes.string,

			/** */
			showPristineErrors: PropTypes.bool,

			/** v1 uses <Tooltip />, v2 uses <EnhancedTooltip /> */
			tooltipVersion: PropTypes.oneOf([1, 2]), // eslint-disable-line no-magic-numbers
			/*
				Version 1 uses css tooltips
				Version 2 uses javascript tooltips
			*/
		};

		getChildContext() {
			return {
				iconClassPrefix: `e-form__control--icon e-form__control--icon-${this.props.iconPos}`,
			};
		}

		constructor(props, context){
			super(props, context);
			this.UID = generateUnique(props);
			this.noLabel = (Composed.displayName === "Checkbox" && !props.hasHeader) || Composed.displayName === "TagGroup";
			this.state = {};

			this.handleInputChange = this.handleInputChange.bind(this);
		}


		/* handleInputChange(string, { action: oneOf['set-value', 'input-change', 'input-blur', 'menu-close']})
		 *	this function is here instead of Select.jsx so that charCount will work
		 *  this function also handles onSelectResetsInput, which was handled in react-select v1 but removed in v2+
		 */
		handleInputChange(inputValue, obj) {
			const { onInputChange, allowCreate, onSelectResetsInput } = this.props
				, allowReset = typeof(onSelectResetsInput) && !onSelectResetsInput && obj && obj.action !== "set-value"
				;
			!!onInputChange && onInputChange(inputValue, obj);
			if (allowCreate || allowReset) {
				this.setState({ inputValue });
				return inputValue;
			}
		}

		/* eslint-disable */
		getLabel() {
			const { label } = this.props;

			return !this.noLabel && !!label ? (
				<label htmlFor={this.UID} className="e-form__control-label">
					{label}
				</label>
			) : null;
		}
		/* eslint-enable */

		/*eslint-disable*/
		getToolTip() {
			const { tip, tooltipVersion } = this.props;

			if (!get(tip, 'description')) { return null; }

			if (tooltipVersion === 1) {
				return <Tooltip {...tip} className="e-form__control-description" />;
			}

			if (tooltipVersion === 2) {
				return (
					<span className="u-form__help" aria-label={tip.description.trim()} >
						<i className="fa fa-info-circle" data-tip data-for={this.UID} data-html={tip.html} aria-label="Get Info" />
						<EnhancedTooltip {...tip} id={this.UID} content={tip.description} />
					</span>
				);
			}
		}
		/* eslint-enable */

		_renderCharCount() {
			const { charMax, name, required, value, meta } = this.props;
			const { inputValue } = this.state;

			if (!charMax) {
				console.warn(`props.showCharCount set to true on input[name=${name}] but props.charMax is not set`);
				return null;
			}

			const length = getValueLength(value, inputValue);

			const classes = classNames('char-count', {
				valid: length <= charMax && (!required || (required && length > 0)),
				invalid: length > charMax || (required && length < 1),
				hidden: !!meta ? getHideCharCountRedux(meta, inputValue) : false,
			});

			return (<div className={classes}>{`${length}/${charMax}`}</div>);
		}

		_renderHeader() {
			const { showCharCount } = this.props;
			const label = this.getLabel();
			const toolTip = this.getToolTip();

			const detail = showCharCount ? this._renderCharCount() : this.props.detail; /*
				This allows us to pass a `detail` prop that lets us render a custom node instead of the character count.

				We let `showCharCount` take priority for backwards compatibility.
			*/

			if (showCharCount || !!label || !!toolTip) {
				return (
					<div className="e-form__control-header">
						{label}
						{toolTip}
						{/* {showCharCount && this._renderCharCount()} */}
						{detail}
					</div>
				);
			}
		}

		_renderMessage(){
			var { meta, showPristineErrors } = this.props
				, classes = { "warning-message": true }
				, $message = null
				, useDirty = Composed.displayName === "TagGroup"
				;

			if (!meta || (!showPristineErrors && !useDirty && !meta.touched) || (!showPristineErrors && useDirty && !meta.dirty)) {
				return null;
			}

			if(meta){
				if(meta.warning){
					classes["has-warning"] = true;
					$message = meta.warning;
				} else if(meta.error){
					classes["has-error"] = true;
					$message = meta.error;
				}
			}

			// if we haven't set a $message, return nothing, this prevents minute height changes
			return $message ? <div className={classNames(classes)}>{$message}</div> : null;
		}

		render(){
			let { label,
				  required,
				  meta,
				  showPristineErrors,
				  tooltipVersion,
				  ...remainderProps
				} = this.props
				, multipleValues = isMultipleValues(this.props)
				, isCheckboxComponent = /checkbox/gi.test(Composed.displayName)
				, isCheckboxList = isCheckboxComponent && /list/gi.test(Composed.displayName)
				, wrapperClasses = {
					[this.props.className]: true,
					required:     !!required,
					"has-tooltip":!!this.getToolTip(),
					"empty-label": !(label),
					"e-form__control-wrapper": true,
					"checkbox-component": isCheckboxComponent && !isCheckboxList,
					"has-label": !this.noLabel,
					"no-label": this.noLabel,
					"text-area-control":(remainderProps.type && remainderProps.type === "textarea"),
					"multiple-values": !!multipleValues,
				}
				, $message = this._renderMessage()
				, mixedProps = Object.assign({}, remainderProps)
				;

			if(meta){
				wrapperClasses = {
					...wrapperClasses,
					"e-form--pristine":    (meta.pristine),
					"e-form--valid":       (meta.valid && (meta.initial || meta.dirty)),
					"e-form--warn":        (meta.warning && (meta.touched || meta.visited || showPristineErrors)),
					"e-form--has-message": !!$message,
				};
				if(!meta.active){
					wrapperClasses["e-form--error"] = ((meta.invalid || meta.error) && (meta.touched || meta.visited || showPristineErrors));
				}
			}

			if (Composed.displayName === "Input") {
				mixedProps.required = required;
			}
			if (Composed.displayName === "Select") {
				mixedProps.onInputChange = this.handleInputChange;
				// onSelectResetsInput was removed from react-select in 2.x and inputValue should be used instead
				if(typeof(remainderProps.onSelectResetsInput) === "boolean" && !remainderProps.onSelectResetsInput && this.state.inputValue){
					mixedProps.inputValue = this.state.inputValue;
				}
			}
			if (Composed.displayName === "Dates") {
				mixedProps.multipleValues = multipleValues;
			}

			if(Composed.displayName === "Checkbox"){
				mixedProps.tooltipVersion = tooltipVersion;
			}

			return (
				<div className={classNames(wrapperClasses)}>
					{this._renderHeader()}
					<Composed
						UID   = {this.UID}
						label = {label}
						ref={cmp => this.composedComponent = cmp}
						{...mixedProps}
					/>
					{$message}
				</div>
			);
		}

	};
}


export default ControlWrapper;
